import type { UseQueryResult } from "react-query";
import type { AxiosError } from "axios";
import memoize from "memoizee";

import {
  OperationResponse,
  OperationsNetworkResponse,
  Operation,
  OperationsDataEnvelope,
} from "../../../../models/operations";
import { convertDataToDataById } from "../../../../helpers/computations";
import { ensureValue } from "../../../../services/util";
import { FetcherQueryKey, QueryFunction } from "../../../../services/query";
import operationsService from "../../../../services/operations";
import {
  numberOfOperationsListsPossiblyActiveAtTheSameTime,
  refinedListOfOperations,
  sortedListOfOperations,
} from "../../../../services/helpers/refinements";

export type OperationsFetcherParams = {
  userId: string | undefined;
  shouldLimitTo1Page: boolean | undefined;
  catalogFlag: boolean | undefined;
};
export type OperationsQueryKey = FetcherQueryKey<OperationsFetcherParams>;

export const operationsQueryKeyPrefix = "[@fabric/core][operations]";

/**
 * Computes the operationsQueryKey from the given params.
 */
export const computeOperationsQueryKey = (
  params: OperationsFetcherParams,
): OperationsQueryKey => [operationsQueryKeyPrefix, params];

/**
 * Fetches the operations using the operationsService.
 */
export const operationsFetcher: QueryFunction<
  OperationsNetworkResponse,
  OperationsFetcherParams
> = async (context) => {
  const shouldLimitTo1Page = ensureValue(
    context.queryKey[1].shouldLimitTo1Page,
    "shouldLimitTo1Page",
  );
  const catalogFlag = ensureValue(
    context.queryKey[1].catalogFlag,
    "catalogFlag",
  );
  return operationsService.getListOfOperationsResponse(
    shouldLimitTo1Page,
    catalogFlag,
  );
};

/**
 * Compute the operationsById derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeOperationsById = memoize(convertDataToDataById, {
  max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
  profileName: "[preOperations.query.helpers] computeOperationsById",
});

/**
 * Compute the operations data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    operations: Operation[],
    operationsById: Record<string, Operation | undefined>,
    totalOperationsCount: number,
  ): OperationsDataEnvelope => {
    return { operations, operationsById, totalOperationsCount };
  },
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[preOperations.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * There is no need for this function itself to be memoized
 * because its called functions are memoized and its result is memoized.
 */
export const computeDataEnvelopeFromResponse = (
  operationsResponse: OperationsNetworkResponse,
): OperationsDataEnvelope => {
  // Compute the derived data for the data envelope.
  // (Don't worry. these compute functions are globally memoized.)
  const operations = sortedListOfOperations(
    refinedListOfOperations(operationsResponse.operations),
  );
  const operationsById = computeOperationsById(operations);
  const dataEnvelope = computeDataEnvelope(
    operations,
    operationsById,
    operationsResponse._metadata.count,
  );
  return dataEnvelope;
};

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in usePreOperationsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<OperationsNetworkResponse, AxiosError>,
): UseQueryResult<OperationsDataEnvelope, AxiosError> => {
  const dataEnvelope = resultOfUseQuery.data
    ? computeDataEnvelopeFromResponse(resultOfUseQuery.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<OperationsDataEnvelope, AxiosError>;
};

/**
 * Compute the hash of the modify dates of the operations.
 *
 * This is important for determining whether the current list
 * of operations is equal to the previous.
 *
 * This is only for use in isDataEqual.
 * Otherwise, the memoization will not function properly.
 */
export const computeModifyDateHash = memoize(
  (operations: OperationResponse[]) => {
    return operations.map((x) => `${x.id}-${x.modify_date}`).join(" ");
  },
  // This needs to be memoized for 2x the
  // numberOfOperationsListsPossiblyActiveAtTheSameTime
  // because we use this to compare the prev to the current.
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime * 2,
    profileName: "[preOperations.query.helpers] computeModifyDateHash",
  },
);

/**
 * Returns whether the current list of operations is equal to the previous.
 *
 * This is determined by comparing the modify dates of the operations.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in usePreOperationsQuery.
 */
export const isDataEqual = (
  prevResponse: OperationsNetworkResponse | undefined,
  response: OperationsNetworkResponse,
): boolean => {
  if (!prevResponse) {
    return false;
  }

  if (prevResponse._metadata.count !== response._metadata.count) {
    return false;
  }

  const prevResponseHash = computeModifyDateHash(prevResponse.operations);
  const responseHash = computeModifyDateHash(response.operations);

  return prevResponseHash === responseHash;
};
